import {
  PLAYER_SESSION_PROTOCOL_VERSION,
  PROGRAMMATIC_PLAYER_CAPABILITY,
  type PlaybackMediaPlaylistEntity,
  PlayerCoordinationSnapshot,
  PlayerCoordinationState,
  PlayerPlaybackOccurrence,
  type PlayerOccurrenceCompletionOutcome,
  type ProgrammaticExposureAuthorizationResult,
} from "@admefy/domain";
import { HttpErrorResponse } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import { WA_WINDOW } from "@ng-web-apis/common";
import {
  catchError,
  concat,
  concatMap,
  defer,
  from,
  filter,
  finalize,
  map,
  merge,
  Observable,
  of,
  retry,
  shareReplay,
  Subject,
  switchMap,
  tap,
  take,
  throwError,
  timeout,
  timer,
} from "rxjs";
import { ApiURL } from "../../tokens";
import { RestService } from "../admefy-cli/admefy-cli.service";
import { PlayerInstanceIdentityService } from "../player-session/player-instance-identity.service";
import { PlayerSessionService } from "../player-session/player-session.service";
import { OfflinePlaybackService } from "../offline-playback/offline-playback.service";

const HEARTBEAT_INTERVAL_MS = 5_000;
const COORDINATION_ACK_TIMEOUT_MS = 10_000;
const COORDINATION_JOIN_TIMEOUT_MS = 10_000;
const PROGRAMMATIC_AUTHORIZATION_TIMEOUT_MS = 5_000;
const MAX_REPORTED_PLAYING_OCCURRENCES = 128;

type Snapshot = PlayerCoordinationSnapshot<PlaybackMediaPlaylistEntity>;
type Occurrence = PlayerPlaybackOccurrence<PlaybackMediaPlaylistEntity>;
type CompletionRequest = {
  occurrence: Occurrence;
  outcome: PlayerOccurrenceCompletionOutcome;
};
type CoordinationAcknowledgement =
  | { kind: "completion"; request: CompletionRequest }
  | { kind: "playing"; occurrence: Occurrence };

@Injectable({ providedIn: "root" })
export class PlayerCoordinationService {
  private readonly apiUrl = inject(ApiURL);
  private readonly rest = inject(RestService);
  private readonly identity = inject(PlayerInstanceIdentityService);
  private readonly playerSession = inject(PlayerSessionService);
  private readonly offlinePlayback = inject(OfflinePlaybackService);
  private readonly window = inject(WA_WINDOW);
  private readonly coordinationState = signal<PlayerCoordinationState | null>(null);
  private readonly occurrenceQueue = signal<Occurrence[]>([]);
  private readonly occurrenceAvailable = new Subject<void>();
  private knownEmptyGeneration?: string;
  private readonly availabilityChanged = new Subject<void>();
  private joinRequest?: Observable<Snapshot | null>;
  private heartbeatIntervalId?: number;
  private heartbeatInFlight = false;
  private streamAbortController?: AbortController;
  private permanentlyUnavailable = false;
  private currentSequence?: number;
  private displayedOccurrenceId?: string;
  private completedSequence = 0;
  private readonly backendAvailable = signal(true);
  private recoveryInProgress = false;
  private recoveryRequest?: Observable<void>;
  private readonly acknowledgementRequests = new Subject<CoordinationAcknowledgement>();
  private readonly pendingCompletions = new Set<string>();
  private readonly queuedPlayingOccurrences = new Set<string>();
  private readonly reportedPlayingOccurrences = new Set<string>();
  private readonly programmaticAuthorizations = new Map<string, string>();
  private readonly authorizationRequests = new Map<
    string,
    Observable<PlaybackMediaPlaylistEntity | null>
  >();
  constructor() {
    this.playerSession.renewed$.subscribe(() => {
      if (!this.isActive()) return;
      this.streamAbortController?.abort();
      this.streamAbortController = undefined;
      this.startStream();
    });
    this.acknowledgementRequests
      .pipe(concatMap((acknowledgement) => this.processAcknowledgement(acknowledgement)))
      .subscribe();
  }

  private processAcknowledgement(acknowledgement: CoordinationAcknowledgement): Observable<void> {
    return acknowledgement.kind === "completion"
      ? this.processCompletion(acknowledgement.request)
      : this.processPlayingReport(acknowledgement.occurrence);
  }

  private processCompletion(request: CompletionRequest): Observable<void> {
    return this.sendCompletion(request).pipe(
      retry({
        delay: (error, retryCount) =>
          this.isRetryableAcknowledgementError(error)
            ? timer(Math.min(1_000 * 2 ** Math.min(retryCount - 1, 5), 30_000))
            : throwError(() => error),
      }),
      catchError((error) => {
        if (this.isCoordinationDesynchronizationError(error, "completion")) {
          return this.requestSessionRecovery(this.coordinationState() ?? undefined).pipe(
            switchMap(() => this.sendCompletion(request)),
            tap(() => this.reportDisplayedOccurrenceIfLeader()),
            catchError((recoveryError) => {
              this.pendingCompletions.delete(request.occurrence.occurrenceId);
              console.warn(
                "Discarding coordinated completion after resynchronization",
                recoveryError,
              );
              return of(undefined);
            }),
          );
        }
        this.pendingCompletions.delete(request.occurrence.occurrenceId);
        console.warn("Discarding terminal coordinated completion", error);
        return of(undefined);
      }),
    );
  }

  private processPlayingReport(occurrence: Occurrence): Observable<void> {
    if (
      this.reportedPlayingOccurrences.has(occurrence.occurrenceId) ||
      !this.canReportPlayingOccurrence(occurrence)
    ) {
      this.queuedPlayingOccurrences.delete(occurrence.occurrenceId);
      return of(undefined);
    }
    return this.sendPlayingReport(occurrence).pipe(
      tap(() => this.rememberSuccessfulPlayingReport(occurrence)),
      retry({
        count: 3,
        delay: (error, retryCount) =>
          this.isRetryableAcknowledgementError(error)
            ? timer(Math.min(100 * 2 ** (retryCount - 1), 1_000))
            : throwError(() => error),
      }),
      catchError((error) => {
        this.reportedPlayingOccurrences.delete(occurrence.occurrenceId);
        if (this.isCoordinationDesynchronizationError(error, "playing")) {
          return this.requestSessionRecovery(this.coordinationState() ?? undefined).pipe(
            switchMap(() => this.retryPlayingReportAfterRecovery(occurrence)),
            tap(() => this.reportDisplayedOccurrenceIfLeader()),
            catchError((recoveryError) => {
              this.queuedPlayingOccurrences.delete(occurrence.occurrenceId);
              console.warn(
                "Unable to report coordinated media playback after resynchronization",
                recoveryError,
              );
              return of(undefined);
            }),
          );
        }
        this.queuedPlayingOccurrences.delete(occurrence.occurrenceId);
        console.warn("Unable to report coordinated media playback", error);
        return of(undefined);
      }),
    );
  }

  private retryPlayingReportAfterRecovery(occurrence: Occurrence): Observable<void> {
    if (!this.canReportPlayingOccurrence(occurrence)) {
      this.queuedPlayingOccurrences.delete(occurrence.occurrenceId);
      return of(undefined);
    }
    return this.sendPlayingReport(occurrence).pipe(
      tap(() => this.rememberSuccessfulPlayingReport(occurrence)),
    );
  }

  private canReportPlayingOccurrence(occurrence: Occurrence): boolean {
    const coordination = this.coordinationState();
    return (
      coordination?.role === "leader" &&
      coordination.generationId === occurrence.generationId &&
      this.displayedOccurrenceId === occurrence.occurrenceId &&
      this.occurrenceQueue().some(
        (queuedOccurrence) =>
          queuedOccurrence.generationId === occurrence.generationId &&
          queuedOccurrence.occurrenceId === occurrence.occurrenceId,
      )
    );
  }

  private rememberSuccessfulPlayingReport(occurrence: Occurrence): void {
    this.queuedPlayingOccurrences.delete(occurrence.occurrenceId);
    if (this.reportedPlayingOccurrences.size >= MAX_REPORTED_PLAYING_OCCURRENCES) {
      const oldestOccurrenceId = this.reportedPlayingOccurrences.values().next().value;
      if (oldestOccurrenceId) {
        this.reportedPlayingOccurrences.delete(oldestOccurrenceId);
      }
    }
    this.reportedPlayingOccurrences.add(occurrence.occurrenceId);
  }

  readonly isActive = computed(() => this.coordinationState() !== null);
  readonly state = this.coordinationState.asReadonly();
  readonly hasBufferedMedia = computed(
    () =>
      this.backendAvailable() &&
      this.occurrenceQueue().some(
        (item) => item.generationId === this.coordinationState()?.generationId,
      ),
  );

  hasPendingCompletions(): boolean {
    return this.pendingCompletions.size > 0;
  }

  canPublishAggregateMetadata(): boolean {
    return this.coordinationState()?.role !== "follower";
  }

  waitForMetadataAuthority(): Observable<boolean> {
    return this.ensureJoined().pipe(
      map(() => this.permanentlyUnavailable || this.coordinationState()?.role === "leader"),
      catchError(() => of(false)),
    );
  }

  getMutationContext(): { instanceId: string; coordinationEpoch: number } | undefined {
    const state = this.coordinationState();
    return state?.role === "leader"
      ? { instanceId: this.identity.value, coordinationEpoch: state.epoch }
      : undefined;
  }

  getProofContext(occurrenceId: string):
    | {
        instanceId: string;
        occurrenceId: string;
        playerProtocolVersion: number;
        authorizationId?: string;
      }
    | undefined {
    if (
      !this.isActive() ||
      !this.occurrenceQueue().some((item) => item.occurrenceId === occurrenceId)
    ) {
      return undefined;
    }
    return {
      instanceId: this.identity.value,
      occurrenceId,
      playerProtocolVersion: PLAYER_SESSION_PROTOCOL_VERSION,
      authorizationId: this.programmaticAuthorizations.get(occurrenceId),
    };
  }

  getCurrentMedia(): Observable<PlaybackMediaPlaylistEntity | null> {
    return this.ensureJoined().pipe(
      switchMap(() => {
        if (!this.isActive()) {
          return of(null);
        }
        const generationId = this.coordinationState()?.generationId;
        const media = this.occurrenceQueue().find(
          (item) => item.generationId === generationId,
        )?.media;
        if (!media && !this.backendAvailable()) {
          throw new Error("PLAYER_COORDINATION_BUFFER_EXHAUSTED");
        }
        const isWaitingForNewGeneration = this.occurrenceQueue().some(
          (item) => item.generationId !== generationId,
        );
        if (media) {
          return of(media);
        }
        const firstAvailableMedia = this.occurrenceAvailable.pipe(
          map(() => {
            const activeGenerationId = this.coordinationState()?.generationId;
            return this.occurrenceQueue().find((item) => item.generationId === activeGenerationId)
              ?.media;
          }),
          filter((nextMedia): nextMedia is PlaybackMediaPlaylistEntity => !!nextMedia),
          take(1),
        );
        return isWaitingForNewGeneration
          ? firstAvailableMedia
          : concat(of(null), firstAvailableMedia);
      }),
      switchMap((media) => this.authorizeForPlayback(media, () => this.getCurrentMedia())),
    );
  }

  getNextMedia(currentIdentifier?: string): Observable<PlaybackMediaPlaylistEntity | null> {
    return this.ensureJoined().pipe(
      switchMap(() => {
        const media = this.selectNextMedia(currentIdentifier);
        if (!media && !this.backendAvailable()) {
          throw new Error("PLAYER_COORDINATION_BUFFER_EXHAUSTED");
        }
        if (media || !this.isActive()) {
          return of(media ?? null);
        }
        return merge(this.occurrenceAvailable, this.availabilityChanged).pipe(
          map(() => {
            const nextMedia = this.selectNextMedia(currentIdentifier);
            if (!nextMedia && !this.backendAvailable()) {
              throw new Error("PLAYER_COORDINATION_BUFFER_EXHAUSTED");
            }
            return nextMedia;
          }),
          filter((nextMedia): nextMedia is PlaybackMediaPlaylistEntity => !!nextMedia),
          take(1),
        );
      }),
    );
  }

  authorizeMediaForPlayback(
    media: PlaybackMediaPlaylistEntity,
  ): Observable<PlaybackMediaPlaylistEntity | null> {
    return this.authorizeForPlayback(media, () => this.getCurrentMedia());
  }

  private selectNextMedia(currentIdentifier?: string): PlaybackMediaPlaylistEntity | undefined {
    const occurrences = this.occurrenceQueue();
    const generationId = this.coordinationState()?.generationId;
    const currentIndex = currentIdentifier
      ? occurrences.findIndex((item) => item.occurrenceId === currentIdentifier)
      : -1;
    if (currentIndex >= 0 && occurrences[currentIndex].generationId !== generationId) {
      return occurrences.find((item) => item.generationId === generationId)?.media;
    }
    return occurrences
      .slice(currentIndex >= 0 ? currentIndex + 1 : 0)
      .find((item) => item.generationId === generationId)?.media;
  }

  markPlaying(media: PlaybackMediaPlaylistEntity): void {
    const occurrence = this.occurrenceQueue().find(
      (item) => item.occurrenceId === media.identifier,
    );
    this.currentSequence = occurrence?.sequence;
  }

  reportPlaying(media: PlaybackMediaPlaylistEntity): void {
    if (!this.isActive()) {
      return;
    }
    this.displayedOccurrenceId = media.identifier;
    const occurrence = this.occurrenceQueue().find(
      (item) => item.occurrenceId === media.identifier,
    );
    if (!occurrence || occurrence.generationId !== this.coordinationState()?.generationId) {
      return;
    }

    this.currentSequence = occurrence.sequence;
    if (this.coordinationState()?.role === "leader") {
      this.enqueuePlayingReport(occurrence);
    }
  }

  private enqueuePlayingReport(occurrence: Occurrence): void {
    if (
      this.reportedPlayingOccurrences.has(occurrence.occurrenceId) ||
      this.queuedPlayingOccurrences.has(occurrence.occurrenceId)
    ) {
      return;
    }
    this.queuedPlayingOccurrences.add(occurrence.occurrenceId);
    this.acknowledgementRequests.next({ kind: "playing", occurrence });
  }

  private sendPlayingReport(occurrence: Occurrence): Observable<void> {
    return defer(() =>
      this.rest.post(
        `/playing/coordination/occurrences/${encodeURIComponent(occurrence.occurrenceId)}/playing`,
        { instanceId: this.identity.value },
      ),
    ).pipe(
      timeout({ first: COORDINATION_ACK_TIMEOUT_MS }),
      map(() => undefined),
    );
  }

  complete(media: PlaybackMediaPlaylistEntity): Observable<void> {
    const occurrence = this.occurrenceQueue().find(
      (item) => item.occurrenceId === media.identifier,
    );
    if (!this.isActive() || !occurrence) {
      if (this.displayedOccurrenceId === media.identifier) {
        this.currentSequence = undefined;
        this.displayedOccurrenceId = undefined;
      }
      return of(undefined);
    }

    if (!this.pendingCompletions.has(occurrence.occurrenceId)) {
      this.pendingCompletions.add(occurrence.occurrenceId);
      // Playback has ended locally even when Redis cannot acknowledge it yet.
      // Keep the request for ordered retries, but never mount this occurrence again.
      this.occurrenceQueue.update((items) =>
        items.filter((item) => item.occurrenceId !== occurrence.occurrenceId),
      );
      this.acknowledgementRequests.next({
        kind: "completion",
        request: { occurrence, outcome: "played" },
      });
    }
    return of(undefined);
  }

  private sendCompletion(request: CompletionRequest): Observable<void> {
    const { occurrence, outcome } = request;
    return defer(() => {
      const requestedGeneration = this.coordinationState()?.generationId;
      return this.rest
        .post(
          `/playing/coordination/occurrences/${encodeURIComponent(occurrence.occurrenceId)}/complete`,
          {
            instanceId: this.identity.value,
            outcome,
          },
        )
        .pipe(
          timeout({ first: COORDINATION_ACK_TIMEOUT_MS }),
          tap((response) => {
            const coordination = response.body?.data as PlayerCoordinationState | undefined;
            const currentGeneration = this.coordinationState()?.generationId;
            // The server may have acknowledged this occurrence before a generation
            // change, with its HTTP response arriving after the new snapshot. Keep
            // the acknowledgement, but do not restore that retired generation.
            if (
              coordination &&
              (coordination.generationId === currentGeneration ||
                requestedGeneration === currentGeneration)
            ) {
              this.applyCoordinationState(coordination);
            }
            this.applyLocalCompletion(occurrence);
            this.pendingCompletions.delete(occurrence.occurrenceId);
          }),
          map(() => undefined),
        );
    });
  }

  private authorizeForPlayback(
    media: PlaybackMediaPlaylistEntity | null,
    continuePlayback: () => Observable<PlaybackMediaPlaylistEntity | null>,
  ): Observable<PlaybackMediaPlaylistEntity | null> {
    if (!media || media.slotType !== "programmatic") return of(media);
    const occurrence = this.occurrenceQueue().find(
      (item) => item.occurrenceId === media.identifier,
    );
    if (!occurrence) return continuePlayback();
    const duration = Number(media.mediaDuration ?? media.duration ?? 10);
    if (
      new Date(media.programmaticExpiresAt).getTime() <=
      Date.now() + (Number.isFinite(duration) ? duration : 10) * 1000
    ) {
      return this.skipOccurrence(occurrence, "skipped_expired", continuePlayback);
    }
    if (this.programmaticAuthorizations.has(occurrence.occurrenceId)) return of(media);
    const inFlight = this.authorizationRequests.get(occurrence.occurrenceId);
    if (inFlight) return inFlight;

    const request = this.rest
      .post(
        `/playing/coordination/occurrences/${encodeURIComponent(occurrence.occurrenceId)}/authorize`,
        { instanceId: this.identity.value },
      )
      .pipe(
        timeout({ first: PROGRAMMATIC_AUTHORIZATION_TIMEOUT_MS }),
        map(
          (response) => response.body?.data as ProgrammaticExposureAuthorizationResult | undefined,
        ),
        catchError(() =>
          of({
            decision: "skip",
            reason: "programmatic_unavailable",
          } as ProgrammaticExposureAuthorizationResult),
        ),
        switchMap((authorization) => {
          if (
            authorization?.decision === "play" &&
            new Date(authorization.expiresAt).getTime() >
              Date.now() + (Number.isFinite(duration) ? duration : 10) * 1000
          ) {
            this.programmaticAuthorizations.set(
              occurrence.occurrenceId,
              authorization.authorizationId,
            );
            return of(media);
          }
          return this.skipOccurrence(
            occurrence,
            authorization?.decision === "skip" && authorization.reason === "delivery_expired"
              ? "skipped_expired"
              : "skipped_unauthorized",
            continuePlayback,
          );
        }),
        finalize(() => this.authorizationRequests.delete(occurrence.occurrenceId)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this.authorizationRequests.set(occurrence.occurrenceId, request);
    return request;
  }

  private skipOccurrence(
    occurrence: Occurrence,
    outcome: PlayerOccurrenceCompletionOutcome,
    continuePlayback: () => Observable<PlaybackMediaPlaylistEntity | null>,
  ): Observable<PlaybackMediaPlaylistEntity | null> {
    if (!this.pendingCompletions.has(occurrence.occurrenceId)) {
      this.pendingCompletions.add(occurrence.occurrenceId);
      this.applyLocalCompletion(occurrence);
      this.acknowledgementRequests.next({
        kind: "completion",
        request: { occurrence, outcome },
      });
    }
    return continuePlayback();
  }

  private applyLocalCompletion(occurrence: Occurrence): void {
    this.currentSequence =
      occurrence.generationId === this.coordinationState()?.generationId &&
      this.currentSequence === occurrence.sequence
        ? undefined
        : this.currentSequence;
    this.displayedOccurrenceId =
      this.displayedOccurrenceId === occurrence.occurrenceId
        ? undefined
        : this.displayedOccurrenceId;
    this.programmaticAuthorizations.delete(occurrence.occurrenceId);
    if (occurrence.generationId === this.coordinationState()?.generationId) {
      this.completedSequence = Math.max(this.completedSequence, occurrence.sequence);
      this.occurrenceQueue.update((items) =>
        items.filter(
          (item) =>
            item.generationId !== occurrence.generationId || item.sequence > occurrence.sequence,
        ),
      );
      return;
    }

    // Sequence numbers restart at one after a generation rotation. A late
    // completion from the previous generation must not advance the new cursor,
    // otherwise its first occurrences are discarded as if already played.
    this.occurrenceQueue.update((items) =>
      items.filter((item) => item.occurrenceId !== occurrence.occurrenceId),
    );
  }

  private isRetryableAcknowledgementError(error: unknown): boolean {
    return (
      !(error instanceof HttpErrorResponse) ||
      error.status === 0 ||
      error.status === 429 ||
      error.status >= 500
    );
  }

  private isCoordinationDesynchronizationError(
    error: unknown,
    acknowledgementKind: "completion" | "playing",
  ): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }
    if (error.status === 404) return true;
    if (error.status !== 400) return false;

    const response = error.error as
      | string
      | {
          message?: unknown;
          data?: { error?: { message?: unknown } };
        }
      | undefined;
    const message =
      typeof response === "string"
        ? response
        : (response?.data?.error?.message ?? response?.message);
    return (
      message ===
      (acknowledgementKind === "completion"
        ? "PLAYER_COORDINATION_NON_CONTIGUOUS_COMPLETION"
        : "PLAYER_COORDINATION_NON_CONTIGUOUS_PLAYING")
    );
  }

  refresh(): Observable<void> {
    if (!this.isActive()) {
      return of(undefined);
    }
    return defer(() => {
      const requestedGeneration = this.coordinationState()?.generationId;
      return this.rest
        .post("/playing/coordination/refresh", { instanceId: this.identity.value })
        .pipe(
          tap((response) => {
            const refreshed = response.body?.data as PlayerCoordinationState | undefined;
            const currentGeneration = this.coordinationState()?.generationId;
            if (
              refreshed &&
              (refreshed.generationId === currentGeneration ||
                requestedGeneration === currentGeneration)
            ) {
              this.applyCoordinationState(refreshed);
            }
          }),
          map(() => undefined),
        );
    });
  }

  private ensureJoined(): Observable<Snapshot | null> {
    if (this.permanentlyUnavailable) {
      return of(null);
    }
    if (this.coordinationState()) {
      return of({
        coordination: this.coordinationState()!,
        occurrences: this.occurrenceQueue(),
      });
    }
    if (this.joinRequest) {
      return this.joinRequest;
    }

    this.joinRequest = defer(() => from(this.identity.ready)).pipe(
      switchMap(() => this.playerSession.ensureValidSession()),
      switchMap((sessionToken) => {
        if (!sessionToken && this.playerSession.isLegacyMode()) {
          this.permanentlyUnavailable = true;
          return of(null);
        }
        if (!sessionToken) return throwError(() => new Error("PLAYER_SESSION_UNAVAILABLE"));
        return this.rest
          .post("/playing/coordination/join", {
            instanceId: this.identity.value,
            protocolVersion: PLAYER_SESSION_PROTOCOL_VERSION,
            capabilities: [PROGRAMMATIC_PLAYER_CAPABILITY],
            ...this.offlineProgressPayload(),
            creativeRotationVersion: 1,
          })
          .pipe(timeout({ first: COORDINATION_JOIN_TIMEOUT_MS }));
      }),
      map((response) => (response ? (response.body?.data as Snapshot) : null)),
      tap((snapshot) => {
        if (snapshot) this.activate(snapshot);
      }),
      catchError((error: unknown) => {
        this.joinRequest = undefined;
        if (error instanceof HttpErrorResponse && (error.status === 404 || error.status === 405)) {
          this.permanentlyUnavailable = true;
          return of(null);
        }
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.joinRequest;
  }

  private activate(snapshot: Snapshot): void {
    this.applySnapshot(snapshot);
    this.startHeartbeat();
    this.startStream();
  }

  private applySnapshot(snapshot: Snapshot): void {
    if (this.isEarlierCoordinationEpoch(snapshot.coordination)) return;
    this.knownEmptyGeneration = snapshot.occurrences.length
      ? undefined
      : snapshot.coordination.generationId;
    if (snapshot.coordination.resyncRequired) {
      this.resetLocalCoordination(snapshot.coordination);
    } else {
      this.applyCoordinationState(snapshot.coordination);
    }
    this.mergeOccurrences(snapshot.occurrences);
    this.reportDisplayedOccurrenceIfLeader();
  }

  private resetLocalCoordination(coordination: PlayerCoordinationState): void {
    if (this.isEarlierCoordinationEpoch(coordination)) return;
    this.completedSequence = Math.max(0, coordination.startSequence - 1);
    this.currentSequence = undefined;
    this.occurrenceQueue.set([]);
    this.reportedPlayingOccurrences.clear();
    this.programmaticAuthorizations.clear();
    this.applyCoordinationState(coordination);
  }

  private mergeOccurrences(incoming: Occurrence[]): void {
    if (!incoming.length) {
      return;
    }
    if (incoming.some((item) => item.generationId === this.knownEmptyGeneration)) {
      this.knownEmptyGeneration = undefined;
    }
    this.occurrenceQueue.update((current) => {
      const byId = new Map(current.map((item) => [item.occurrenceId, item]));
      for (const occurrence of incoming) {
        if (
          occurrence.sequence > this.completedSequence &&
          !this.pendingCompletions.has(occurrence.occurrenceId)
        ) {
          byId.set(occurrence.occurrenceId, occurrence);
        }
      }
      return [...byId.values()].sort((left, right) => left.sequence - right.sequence);
    });
    this.occurrenceAvailable.next();
  }

  private isEarlierCoordinationEpoch(coordination: PlayerCoordinationState): boolean {
    const current = this.coordinationState();
    return (
      current?.generationId === coordination.generationId && coordination.epoch < current.epoch
    );
  }

  private applyCoordinationState(coordination: PlayerCoordinationState): void {
    const previous = this.coordinationState();
    // Within one generation the election epoch only advances. Delayed ACKs
    // from an earlier election must not demote the current leader again.
    if (this.isEarlierCoordinationEpoch(coordination)) return;
    const previousGeneration = previous?.generationId;
    const previousRole = previous?.role;
    if (previousGeneration && previousGeneration !== coordination.generationId) {
      // A long occurrence may still be visible after several plan rotations.
      // Its identity remains valid even after its generation is no longer the
      // immediately previous one and its numeric cursor has been cleared.
      const mountedOccurrence =
        this.occurrenceQueue().find((item) => item.occurrenceId === this.displayedOccurrenceId) ??
        this.occurrenceQueue().find(
          (item) =>
            item.generationId === previousGeneration && item.sequence === this.currentSequence,
        );
      this.completedSequence = Math.max(0, coordination.startSequence - 1);
      // The mounted occurrence may finish naturally after the backend rotates the
      // generation, but its numeric sequence is not a cursor in the new generation.
      // Reporting it in the next heartbeat can make the backend skip the new
      // generation's leading occurrences because sequence numbers restart at one.
      this.currentSequence = undefined;
      this.occurrenceQueue.set(mountedOccurrence ? [mountedOccurrence] : []);
      this.reportedPlayingOccurrences.clear();
    }
    this.coordinationState.set(coordination);
    if (previousRole === "follower" && coordination.role === "leader") {
      this.reportDisplayedOccurrenceIfLeader();
    }
  }

  private reportDisplayedOccurrenceIfLeader(): void {
    const coordination = this.coordinationState();
    if (coordination?.role !== "leader") {
      return;
    }
    const currentOccurrence = this.occurrenceQueue().find(
      (item) =>
        item.generationId === coordination.generationId &&
        item.occurrenceId === this.displayedOccurrenceId,
    );
    if (currentOccurrence) {
      this.enqueuePlayingReport(currentOccurrence);
    }
  }

  private startHeartbeat(): void {
    if (this.heartbeatIntervalId !== undefined) {
      return;
    }
    this.heartbeatIntervalId = this.window.setInterval(() => {
      const state = this.coordinationState();
      if (!state || this.heartbeatInFlight) {
        return;
      }
      this.heartbeatInFlight = true;
      this.rest
        .post("/playing/coordination/heartbeat", {
          instanceId: this.identity.value,
          protocolVersion: PLAYER_SESSION_PROTOCOL_VERSION,
          capabilities: [PROGRAMMATIC_PLAYER_CAPABILITY],
          generationId: state.generationId,
          currentSequence: this.currentSequence,
          completedSequence: this.completedSequence,
        })
        .pipe(
          timeout(10_000),
          map((response) => response.body?.data as PlayerCoordinationState),
          finalize(() => {
            this.heartbeatInFlight = false;
          }),
        )
        .subscribe({
          next: (coordination) => {
            const currentGeneration = this.coordinationState()?.generationId;
            if (
              this.isEarlierCoordinationEpoch(coordination) ||
              (coordination.generationId !== currentGeneration &&
                state.generationId !== currentGeneration)
            )
              return;
            const reconcileOfflineProgress =
              !this.backendAvailable() &&
              !!this.offlineProgressPayload().offlineCreativeProgress?.length;
            if (
              reconcileOfflineProgress ||
              (this.knownEmptyGeneration !== coordination.generationId &&
                !this.occurrenceQueue().some(
                  (item) => item.generationId === coordination.generationId,
                ))
            ) {
              // A recovered heartbeat does not carry occurrences. Refill an
              // exhausted buffer even if the event stream never reconnects.
              // Reconcile offline turns before exposing an old online buffer.
              if (!this.recoveryInProgress) {
                this.requestSessionRecovery(
                  coordination,
                  !reconcileOfflineProgress && !coordination.resyncRequired,
                )
                  .pipe(tap(() => this.reportDisplayedOccurrenceIfLeader()))
                  .subscribe({ error: () => undefined });
              }
              return;
            }
            this.backendAvailable.set(true);
            this.availabilityChanged.next();
            if (coordination.resyncRequired) {
              if (!this.recoveryInProgress) {
                this.requestSessionRecovery(coordination)
                  .pipe(tap(() => this.reportDisplayedOccurrenceIfLeader()))
                  .subscribe({ error: () => undefined });
              }
              return;
            }
            this.applyCoordinationState(coordination);
            this.reportDisplayedOccurrenceIfLeader();
          },
          error: (error) => {
            const current = this.coordinationState();
            if (current?.generationId !== state.generationId || current.epoch > state.epoch) return;
            this.backendAvailable.set(false);
            this.availabilityChanged.next();
            console.warn("Coordinated player heartbeat failed", error);
            if (
              error instanceof HttpErrorResponse &&
              error.status === 404 &&
              !this.recoveryInProgress
            ) {
              this.requestSessionRecovery()
                .pipe(tap(() => this.reportDisplayedOccurrenceIfLeader()))
                .subscribe({ error: () => undefined });
            }
          },
        });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private startStream(): void {
    if (this.streamAbortController) {
      return;
    }
    const token = this.playerSession.currentAccessToken();
    if (!token) {
      return;
    }
    this.streamAbortController = new AbortController();
    const afterSequence = Math.max(0, this.completedSequence);
    const url = new URL(`${this.apiUrl}/playing/coordination/stream`);
    url.searchParams.set("instanceId", this.identity.value);
    url.searchParams.set("afterSequence", String(afterSequence));
    url.searchParams.set("generationId", this.coordinationState()?.generationId ?? "");
    url.searchParams.set("ngsw-bypass", "true");
    void this.consumeStream(url, token, this.streamAbortController);
  }

  private offlineProgressPayload(): {
    offlineCreativeProgress?: { campaignId: string; snapshotId: string; ordinal: number }[];
  } {
    const progress = this.offlinePlayback?.getRotationProgress() ?? [];
    return progress.length ? { offlineCreativeProgress: progress } : {};
  }

  private requestSessionRecovery(
    coordination?: PlayerCoordinationState,
    preserveBufferedPlayback = false,
  ): Observable<void> {
    if (this.recoveryRequest) {
      return this.recoveryRequest;
    }

    this.recoveryInProgress = true;
    if (coordination) {
      if (preserveBufferedPlayback) this.applyCoordinationState(coordination);
      else this.resetLocalCoordination(coordination);
    }
    const previousStream = this.streamAbortController;
    this.streamAbortController = undefined;
    previousStream?.abort();

    const recoveryRequest = defer(() => {
      const requestedGeneration = this.coordinationState()?.generationId;
      return this.rest
        .post("/playing/coordination/join", {
          instanceId: this.identity.value,
          protocolVersion: PLAYER_SESSION_PROTOCOL_VERSION,
          capabilities: [PROGRAMMATIC_PLAYER_CAPABILITY],
          ...this.offlineProgressPayload(),
          creativeRotationVersion: 1,
        })
        .pipe(
          timeout({ first: COORDINATION_JOIN_TIMEOUT_MS }),
          map((response) => response.body?.data as Snapshot),
          tap((snapshot) => {
            const currentGeneration = this.coordinationState()?.generationId;
            if (
              this.isEarlierCoordinationEpoch(snapshot.coordination) ||
              (snapshot.coordination.generationId !== currentGeneration &&
                requestedGeneration !== currentGeneration)
            )
              return;
            this.backendAvailable.set(true);
            if (preserveBufferedPlayback) this.applySnapshot(snapshot);
            else {
              this.knownEmptyGeneration = snapshot.occurrences.length
                ? undefined
                : snapshot.coordination.generationId;
              this.resetLocalCoordination(snapshot.coordination);
              this.mergeOccurrences(snapshot.occurrences);
            }
          }),
        );
    }).pipe(
      map(() => undefined),
      finalize(() => {
        this.recoveryRequest = undefined;
        this.recoveryInProgress = false;
        if (this.isActive()) {
          this.startStream();
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.recoveryRequest = recoveryRequest;
    return recoveryRequest;
  }

  private async consumeStream(
    url: URL,
    token: string,
    abortController: AbortController,
  ): Promise<void> {
    try {
      const response = await fetch(url, {
        cache: "no-store",
        headers: { Accept: "text/event-stream", Authorization: `Bearer ${token}` },
        signal: abortController.signal,
      });
      if (!response.ok || !response.body) {
        throw new Error(`Coordination stream failed with status ${response.status}`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split(/\r?\n\r?\n/);
        buffer = blocks.pop() ?? "";
        for (const block of blocks) this.applyStreamEvent(block);
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        console.warn("Coordination stream unavailable; buffered playback remains active", error);
      }
    } finally {
      if (this.streamAbortController === abortController) {
        this.streamAbortController = undefined;
        if (!abortController.signal.aborted && this.isActive()) {
          this.window.setTimeout(() => this.startStream(), 1_000);
        }
      }
    }
  }

  private applyStreamEvent(block: string): void {
    let eventName = "message";
    const data: string[] = [];
    for (const line of block.split(/\r?\n/)) {
      const separator = line.indexOf(":");
      const field = separator < 0 ? line : line.slice(0, separator);
      const value = separator < 0 ? "" : line.slice(separator + 1).replace(/^\s/, "");
      if (field === "event") eventName = value;
      if (field === "data") data.push(value);
    }
    if (!data.length) return;
    const payload = data.join("\n");
    // Nest's SSE exception handler sends error messages as plain text.
    // Recover a missing session instead of endlessly reconnecting the same one.
    if (eventName === "error") {
      if (
        payload === "PLAYER_COORDINATION_SESSION_NOT_FOUND" ||
        payload === "PLAYER_COORDINATION_NOT_INITIALIZED"
      ) {
        if (!this.recoveryInProgress) {
          this.requestSessionRecovery()
            .pipe(tap(() => this.reportDisplayedOccurrenceIfLeader()))
            .subscribe({ error: () => undefined });
        }
        return;
      }
      throw new Error(payload);
    }
    if (eventName !== "coordination-state" && eventName !== "playback-occurrence") return;
    const parsed = JSON.parse(payload) as unknown;
    if (eventName === "coordination-state") {
      this.applyCoordinationState(parsed as PlayerCoordinationState);
    } else if (eventName === "playback-occurrence") {
      this.mergeOccurrences([parsed as Occurrence]);
    }
  }
}
